"use client";

import { memo, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AuthPasswordForm } from "@/components/auth/AuthPasswordForm";
import { AuthRegisterForm } from "@/components/auth/AuthRegisterForm";
import { AuthSetPasswordForm } from "@/components/auth/AuthSetPasswordForm";
import { AuthOtpForm } from "@/components/auth/AuthOtpForm";
import { AuthResetPasswordForm } from "@/components/auth/AuthResetPasswordForm";
import { authService } from "@/lib/api/services";
import { useAuthStore } from "@/store/auth";
import type { ModalView } from "../donationModalReducer";

export interface DonationAuthStepProps {
  view: ModalView;
  phoneNumber: string;
  onSuccess: () => void;
  onBack: () => void;
  onUserExists: () => void;
  onViewChange: (view: ModalView) => void;
  cartMode: boolean;
  isPreparingPayment: boolean;
  t: (key: string) => string;
}

const PreparingOverlay = ({ t }: { t: (key: string) => string }) => (
  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-background/90 py-8">
    <Loader2 className="h-10 w-10 animate-spin text-primary mb-3" />
    <p className="text-sm text-muted-foreground">
      {t("checkout.creatingDonation")}
    </p>
  </div>
);

function DonationAuthStepComponent({
  view,
  phoneNumber,
  onSuccess,
  onBack,
  onUserExists,
  onViewChange,
  cartMode,
  isPreparingPayment,
}: DonationAuthStepProps) {
  const t = useTranslations();
  const showAuthFeedback = useAuthStore((s) => s.showAuthFeedback);
  const showOverlay = cartMode && isPreparingPayment;

  const [isSendingResetCode, setIsSendingResetCode] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const sendResetCode = useCallback(async () => {
    setIsSendingResetCode(true);
    setResetError(null);
    try {
      const res = await authService.forgotPassword({ mobile: phoneNumber });
      if (res.success) {
        onViewChange("enter-otp");
      } else {
        setResetError(res.message || res.error || t("auth.loginError"));
      }
    } catch {
      setResetError(t("auth.loginError"));
    } finally {
      setIsSendingResetCode(false);
    }
  }, [phoneNumber, onViewChange, t]);

  const handleResendCode = useCallback(async () => {
    await authService.forgotPassword({ mobile: phoneNumber });
  }, [phoneNumber]);

  const handleOtpSubmit = useCallback(
    (code: string) => {
      setOtpCode(code);
      setOtpError(null);
      setIsVerifyingOtp(false);
      onViewChange("reset-password");
    },
    [onViewChange],
  );

  const handleResetSuccess = useCallback(() => {
    showAuthFeedback(t("auth.passwordResetSuccess"));
    onViewChange("password");
  }, [showAuthFeedback, onViewChange, t]);

  const handleResetBack = useCallback(() => {
    onViewChange("enter-otp");
  }, [onViewChange]);

  const handleOtpBack = useCallback(() => {
    setOtpError(null);
    onViewChange("password");
  }, [onViewChange]);

  if (view === "password") {
    return (
      <div className="relative">
        {showOverlay && <PreparingOverlay t={t} />}
        <AuthPasswordForm
          phone={phoneNumber}
          onSuccess={onSuccess}
          onBack={onBack}
          onForgotPassword={() => void sendResetCode()}
          isSendingResetCode={isSendingResetCode}
        />
        {resetError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{resetError}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  if (view === "enter-otp") {
    return (
      <div className="relative">
        {showOverlay && <PreparingOverlay t={t} />}
        <AuthOtpForm
          phone={phoneNumber}
          onSubmit={handleOtpSubmit}
          onResendCode={handleResendCode}
          error={otpError}
          isSubmitting={isVerifyingOtp}
        />
      </div>
    );
  }

  if (view === "reset-password") {
    return (
      <div className="relative">
        {showOverlay && <PreparingOverlay t={t} />}
        <AuthResetPasswordForm
          phone={phoneNumber}
          code={otpCode}
          onSuccess={handleResetSuccess}
          onBack={handleResetBack}
          showBackButton={false}
        />
      </div>
    );
  }

  if (view === "register") {
    return (
      <div className="relative">
        {showOverlay && <PreparingOverlay t={t} />}
        <AuthRegisterForm
          phone={phoneNumber}
          onSuccess={onSuccess}
          onBack={onBack}
          onUserExists={onUserExists}
        />
      </div>
    );
  }

  if (view === "set-password") {
    return (
      <div className="relative">
        {showOverlay && <PreparingOverlay t={t} />}
        <AuthSetPasswordForm
          phone={phoneNumber}
          onSuccess={onSuccess}
          onBack={onBack}
        />
      </div>
    );
  }

  return null;
}

export const DonationAuthStep = memo(DonationAuthStepComponent);
